import React, { Suspense, useContext } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { MLWithdrawalAutomationGlobalControlsPage } from '@/routes/Administration/PaymentTools/MLWithdrawalAutomation/MLWithdrawalAutomationGlobalControlsPage'
import { MLWithdrawalAutomationOverridesPage } from '@/routes/Administration/PaymentTools/MLWithdrawalAutomation/MLWithdrawalAutomationOverridesPage'
import { TransactionProcessingMetricsPage } from '@/routes/Administration/PaymentTools/TransactionProcessingMetrics/TransactionProcessingMetricsPage'
import { DashboardErrorPage } from '@/routes/Dashboard/DashboardErrorPage'
import { RewardPrograms } from '@/routes/Marketing/RewardsHub/RewardPrograms/RewardPrograms'
import { RewardsTypeManager } from '@/routes/Marketing/RewardsHub/RewardsTypeManager'
import { Spinner } from 'components/Spinner'
import ServiceUnavailable from 'routes/503/ServiceUnavailable'
import { Emails } from 'routes/Administration/Emails/Emails'
import { AllowedPaymentProviders } from 'routes/Administration/PaymentTools/PaymentControls/AllowedPaymentProviders/AllowedPaymentProviders'
import PaymentControlsPSPCheckStatePage from 'routes/Administration/PaymentTools/PaymentControls/PaymentControlsPSPCheckState/PaymentControlsPSPCheckStatePage'
import { PlatformDownloadLinksPage } from 'routes/Administration/PlatformDownloadLinks/PlatformDownloadLinksPage'
import RolesPage from 'routes/Administration/Roles/RolesPage'
import Teams from 'routes/Administration/Teams/Teams'
import ResolveDividendsRoute from 'routes/Brokerage/Dividends/DividendsSlug'
import SwapFreeRating from 'routes/Brokerage/SwapFreeRating/SwapFreeRatingPage'
// import ResolveAppropriatenessTestRoute from 'routes/Compliance/AppropriatenessTest/AppropriatenessTestPageSlug'
import CountryRiskPage from 'routes/Compliance/CountryRisk/CountryRiskPage'
import WorldCheckCasesPage from 'routes/Compliance/WorldCheckCases/WorldCheckCasesPage'
import LoginPage from 'routes/Login/LoginPage'
import { IbLandingDetailsPage } from 'routes/Marketing/IbLandingPage/IbLandingDetailsPage'
import IbLandingPage from 'routes/Marketing/IbLandingPage/IbLandingPage'
import ReferralMaterialsCategoryDetailsPage from 'routes/Marketing/referralMaterialsPage/ReferralMaterialsCategoriesDetailsPage'
import ReferralMaterialsCategoriesPage from 'routes/Marketing/referralMaterialsPage/ReferralMaterialsCategoriesPage'
import SGLLinkGeneratorPage from 'routes/Marketing/SGLLinkGenerator/SGLLinkGeneratorPage'
import PageNotFound from 'routes/PageNotFound/PageNotFound'
import { FeatureFlagDetailsPage } from 'routes/Product/FeatureFlags/Details/FeatureFlagDetailsPage'
import { FeatureFlags } from 'routes/Product/FeatureFlags/FeatureFlags'
import { PlatformVersions } from 'routes/Product/PlatformVersions/PlatformVersions'
import { ProductVersions } from 'routes/Product/Versions/Versions'
import { UserProfilePage } from 'routes/Profile/UserProfilePage'
import AccountGroups from 'routes/Records/AccountGroup/AccountGroup'
import { AffiliateSlug } from 'routes/Records/Affiliates/RecordsAffiliates/Details/AffiliateSlug'
import { RecordsAffiliatesPage } from 'routes/Records/Affiliates/RecordsAffiliates/RecordsAffiliatesPage'
import ClientTestFilledWindow from 'routes/Records/Clients/Details/ClientTests/ClientTestFilledWindow'
import ClientTestFormWindow from 'routes/Records/Clients/Details/ClientTests/ClientTestFormWindow'
import ResolveLeadRoute from 'routes/Records/Leads/Details/LeadSlug'
import PaymentAgentDetailsPage from 'routes/Records/PaymentAgent/Details/PaymentAgentDetailsPage'
import PaymentAgentsOverview from 'routes/Records/PaymentAgent/PaymentAgentsOverview'
import Wallets from 'routes/Records/Wallets/Wallets'
import RecordsCurrencyMarkups from 'routes/RecordsCurrencyMarkups/components/RecordsCurrencyMarkups'
import SearchPage from 'routes/Search/SearchPage'
import TicketBackOfficeTabs from 'routes/Tickets/TicketBackOfficeTabs'
import TicketComplianceTabs from 'routes/Tickets/TicketComplianceTabs'
import TicketPaymentTabs from 'routes/Tickets/TicketPaymentTabs'
import TicketsBackOffice from 'routes/Tickets/TicketsBackOffice'
import TicketsBrokerage from 'routes/Tickets/TicketsBrokerage'
import TicketsCompliance from 'routes/Tickets/TicketsCompliance'
import TransactionsETDTab from 'routes/Transactions/Create/TransactionETDTab'
import TransactionsTab from 'routes/Transactions/Create/TransactionTab'
import DepositWithdrawalTransactionOverview from 'routes/Transactions/DepositWithdrawalTransactionOverview'
import TransactionIBCashbackSettingsOverview from 'routes/Transactions/TransactionIBCashbackSettingsOverview'
import TransactionUnsubscribeRequestsOverview from 'routes/Transactions/TransactionUnsubscribeRequestsOverview'
import WalletTradingAccountTransferOverview from 'routes/Transactions/WalletTradingAccountTransferOverview'
import WalletTransactionsOverview from 'routes/Transactions/WalletTransactionsOverview'
import { Toaster } from 'sonner'
import { AuthSessionContext } from 'utils/AuthContext'
import { AuthorizedRoute } from 'utils/AuthRoute'
import { isFeatureEnabled, links } from 'utils/featureToggles'
import { TenantFeature } from 'utils/Tenant'

import { MainLayout } from './MainLayout'
import { isRouteAllowed } from './permissions'
import { ContactDetailsPage } from './routes/Administration/ContactDetails/ContactDetailsPage'
import AdministrationCountriesProducts from './routes/Administration/Countries/AdministrationCountriesProducts'
import Countries from './routes/Administration/Countries/Countries'
import TradingAccountsCountries from './routes/Administration/Countries/TradingAccountsCountries/TradingAccountsCountries'
import InterestRatesEligibilityPage from './routes/Administration/InterestRates/InterestRatesEligibilityPage'
import InterestRatesThresholdsPage from './routes/Administration/InterestRates/InterestRatesThresholdsPage'
import InterestRatesWalletsConfigurationPage from './routes/Administration/InterestRates/InterestRatesWalletsConfigurationPage'
import PaymentProvidersConfigPage from './routes/Administration/PaymentProviders/PaymentProvidersConfigPage'
import { ResolvePaymentProviderSlug } from './routes/Administration/PaymentProviders/PaymentProviderSlug'
import { PaymentProvidersOrderingPage } from './routes/Administration/PaymentProviders/PaymentProvidersOrderingPage'
import PaymentControlsPSPCheckStateDetailsPage from './routes/Administration/PaymentTools/PaymentControls/PaymentControlsPSPCheckState/PaymentControlsPSPCheckStateDetailsPage'
import WithdrawalThresholdsPage from './routes/Administration/PaymentTools/WithdrawalThresholds/WithdrawalThresholdsPage'
import RateMarkupsPage from './routes/Administration/RateMarkups/RateMarkupsPage'
import { ResolveSubscriptionRoute } from './routes/Administration/Subscriptions/SubscriptionSlug'
import { SubscriptionsPage } from './routes/Administration/Subscriptions/SubscriptionsPage'
import AdministrationUsers from './routes/Administration/Users/AdministrationUsers'
import AdministrationUserTeams from './routes/Administration/UserTeams/AdministrationUserTeams'
import AdministrationWallets from './routes/Administration/Wallets/AdministrationWallets'
import { BulkRestrictionsPage } from './routes/Brokerage/BulkRestrictions/BulkRestrictionsPage'
import RecordDealsPage from './routes/Brokerage/Deals/DealsPage'
import { InstrumentsPage } from './routes/Brokerage/Instruments/InstrumentsPage'
import IntroducingBrokerProfilePage from './routes/Brokerage/IntroducingBrokerProfiles/Details/IntroducingBrokerProfile/IntroducingBrokerProfilePage'
import IntroducingBrokerProfiles from './routes/Brokerage/IntroducingBrokerProfiles/IntroducingBrokerProfiles'
import { LeveragesPage } from './routes/Brokerage/Platforms/LeveragesPage'
import { BrokeragePlatformServersConfigurationPage } from './routes/Brokerage/Platforms/PlatformConfiguration/BrokeragePlatformConfigurationPage'
import { BrokeragePlatformSessionsPage } from './routes/Brokerage/Platforms/PlatformsSessions/BrokeragePlatformSessionsPage'
import { BrokeragePlatformSwapsPage } from './routes/Brokerage/Platforms/TransactionNegativeEquity/BrokeragePlatformSwapsPage'
import ReferralsRestrictionsPage from './routes/Brokerage/ReferralsRestrictions/ReferralsRestrictionsPage'
import AppropriatenessTestDetailsPage from './routes/Compliance/AppropriatenessTest/AppropriatenessTestDetailsPage'
import AppropriatenessTestPage from './routes/Compliance/AppropriatenessTest/AppropriatenessTestPage'
import DisclaimerLossPercentagePage from './routes/Compliance/DisclaimerLossPercentage/DisclaimerLossPercentagePage'
import Dashboard, { DashboardLayout } from './routes/Dashboard/Dashboard'
import BonusCampaignModulePage from './routes/Marketing/BonusModule/BonusCampaignModulePage'
import ResolveBonusCampaignRoute from './routes/Marketing/BonusModule/BonusCampaignSlug'
import ResolveCampaignRoute from './routes/Marketing/CampaignSlug'
import CampaignsOverviewPage from './routes/Marketing/CampaignsOverviewPage'
import MarketingNotificationsOverviewPage from './routes/Marketing/Notifications/MarketingNotificationsOverviewPage'
import ResolveNotificationRoute from './routes/Marketing/Notifications/NotificationSlug'
import ReferralMaterialsDetailsPage from './routes/Marketing/referralMaterialsPage/referralMaterialsDetailsPage'
import ReferralMaterialsPage from './routes/Marketing/referralMaterialsPage/referralMaterialsPage'
import { ResolveRewardPrograms } from './routes/Marketing/RewardsHub/RewardPrograms/ResolveRewardPrograms'
import AccountGroupDetailsPage from './routes/Records/AccountGroup/Details/AccountGroupDetailsPage'
import ResolveClientRoute from './routes/Records/Clients/Details/ClientDetailsSlug'
import { RecordsClientsPage } from './routes/Records/Clients/RecordsClients/RecordsClients'
import ResolveIBRoute from './routes/Records/IB/IntroducingBrokerSlug'
import IntroducingBrokersOverview from './routes/Records/IB/IntroducingBrokersOverview/IntroducingBrokersOverview'
import CreateLeadPage from './routes/Records/Leads/Details/CreateLeadPage'
import RecordLeadsPage from './routes/Records/Leads/LeadsPage'
import DemoTradingAccountRoute from './routes/Records/TradingAccounts/Details/DemoTradingAccountSlug'
import ResolveTradingAccountRoute from './routes/Records/TradingAccounts/Details/TradingAccountSlug'
import WelcomeTradingAccountRoute from './routes/Records/TradingAccounts/Details/WelcomeTradingAccountSlug'
import { DemoTradingAccounts } from './routes/Records/TradingAccounts/TradingAccounts/DemoTradingAccounts'
import { LiveTradingAccounts } from './routes/Records/TradingAccounts/TradingAccounts/LiveTradingAccounts'
import { WelcomeTradingAccounts } from './routes/Records/TradingAccounts/TradingAccounts/WelcomeTradingAccounts'
import ResolveWalletRoute from './routes/Records/Wallets/WalletSlug'
import RecordsExchangeRates from './routes/RecordsExchangeRates/RecordsExchangeRates'
import TicketBrokerageDetails from './routes/Tickets/TicketBrokerageDetails'
import TicketSalesTabs from './routes/Tickets/TicketSalesTabs'
import TicketsMyTickets from './routes/Tickets/TicketsMyTickets'
import TicketsPayments from './routes/Tickets/TicketsPayments'
import TicketsSales from './routes/Tickets/TicketsSales'
import TicketsTranslation from './routes/Tickets/TicketsTranslation'
import TicketTranslationDetails from './routes/Tickets/TicketTranslationDetails'
import BonusTransactionsOverview from './routes/Transactions/BonusTransactionsOverview'
import ResolveTransactionDetailsRoute from './routes/Transactions/Details/ResolveTransactionDetailsRoute'
import PaymentAgentTransactionsOverview from './routes/Transactions/PaymentAgentTransactionsOverview'
import TransactionBulkTransactionFileUploadHistoryOverview from './routes/Transactions/TransactionBulkTransactionFileUploadHistoryOverview'
import TransactionBulkTransactionWithdrawalsOverview from './routes/Transactions/TransactionBulkTransactionWithdrawalsOverview'
import TransactionIBCashbacksOverview from './routes/Transactions/TransactionIBCashbacksOverview'
import TransactionIBCommissionsOverview from './routes/Transactions/TransactionIBCommissionsOverview'
import TransactionTradingAccountAdjustmentsOverview from './routes/Transactions/TransactionTradingAccountAdjustmentsOverview'
import WalletSingleAndMultiTransactionsOverview from './routes/Transactions/WalletSingleAndMultiTransactionsOverview/WalletSingleAndMultiTransactionsOverview'

const MainContainer: React.FC = (props) => {
  const [session] = useContext(AuthSessionContext)
  const permissions = session?.permissions || []
  return (
    <Suspense fallback={<Spinner />}>
      <MainLayout {...props}>
        <Routes>
          <Route
            path={links.profileSettings.url}
            element={<AuthorizedRoute element={<UserProfilePage />} />}
          />
          <Route path={links.serverErrorServiceUnavailable.url} element={<ServiceUnavailable />} />
          <Route path='404' element={<PageNotFound />} />
          <Route path={links.serverErrorServiceUnavailable.url} element={<ServiceUnavailable />} />
          <Route
            path={links.clientTestFilledWindow.detailsUrl}
            element={<ClientTestFilledWindow />}
          />
          <Route
            path={links.clientEditTestFormWindow.detailsUrl}
            element={<AuthorizedRoute element={<ClientTestFormWindow />} />}
          />
          <Route
            path={links.clientNewTestFormWindow.detailsUrl}
            element={<AuthorizedRoute element={<ClientTestFormWindow />} />}
          />
          {!session && <Route path='*' element={<LoginPage />} />}
          {session && (
            <>
              <Route
                path={links.dashboard.url}
                element={<AuthorizedRoute element={<DashboardLayout />} />}
              >
                <Route index element={<Dashboard />} />
                <Route path='error' element={<DashboardErrorPage />} />
              </Route>
              <Route
                path={links.search.url}
                element={<AuthorizedRoute element={<SearchPage />} />}
              />
              {isRouteAllowed(links.recordsAffiliates.url, permissions) && (
                <Route
                  path={links.recordsAffiliates.url}
                  element={<AuthorizedRoute element={<RecordsAffiliatesPage />} />}
                />
              )}
              <Route
                path={links.recordsAffiliates.detailsUrl}
                element={<AuthorizedRoute element={<AffiliateSlug />} />}
              />

              <Route
                path={links.recordsClients.detailsUrl}
                element={<AuthorizedRoute element={<ResolveClientRoute />} />}
              />

              {isRouteAllowed(links.recordsClients.url, permissions) && (
                <Route
                  path={links.recordsClients.url}
                  element={<AuthorizedRoute element={<RecordsClientsPage />} />}
                />
              )}
              <Route
                path={links.recordsWallets.detailsUrl}
                element={<AuthorizedRoute element={<ResolveWalletRoute />} />}
              />
              {isRouteAllowed(links.recordsWallets.url, permissions) && (
                <Route
                  path={links.recordsWallets.url}
                  element={<AuthorizedRoute element={<Wallets />} />}
                />
              )}

              {isRouteAllowed(links.recordsTradingAccounts.url, permissions) && (
                <Route
                  path={links.recordsTradingAccounts.url}
                  element={<AuthorizedRoute element={<LiveTradingAccounts />} />}
                />
              )}
              <Route
                path={links.recordsTradingAccounts.detailsUrl}
                element={<AuthorizedRoute element={<ResolveTradingAccountRoute />} />}
              />

              {isRouteAllowed(links.recordsDemoAccounts.url, permissions) && (
                <Route
                  path={links.recordsDemoAccounts.url}
                  element={<AuthorizedRoute element={<DemoTradingAccounts />} />}
                />
              )}
              <Route
                path={links.recordsDemoAccounts.detailsUrl}
                element={<AuthorizedRoute element={<DemoTradingAccountRoute />} />}
              />

              {isRouteAllowed(links.recordsWelcomeAccounts.url, permissions) && (
                <Route
                  path={links.recordsWelcomeAccounts.url}
                  element={<AuthorizedRoute element={<WelcomeTradingAccounts />} />}
                />
              )}
              <Route
                path={links.recordsWelcomeAccounts.detailsUrl}
                element={<AuthorizedRoute element={<WelcomeTradingAccountRoute />} />}
              />

              <Route
                path={links.recordsAccountGroups.detailsUrl}
                element={<AuthorizedRoute element={<AccountGroupDetailsPage />} />}
              />
              {isRouteAllowed(links.recordsAccountGroups.url, permissions) && (
                <Route
                  path={links.recordsAccountGroups.url}
                  element={<AuthorizedRoute element={<AccountGroups />} />}
                />
              )}
              <Route
                path={links.brokerageIBProfiles.detailsUrl}
                element={<AuthorizedRoute element={<IntroducingBrokerProfilePage />} />}
              />
              {isRouteAllowed(links.brokerageIBProfiles.url, permissions) && (
                <Route
                  path={links.brokerageIBProfiles.url}
                  element={<AuthorizedRoute element={<IntroducingBrokerProfiles />} />}
                />
              )}
              {isRouteAllowed(links.brokerageBulkRestrictions.url, permissions) && (
                <Route
                  path={links.brokerageBulkRestrictions.url}
                  element={<AuthorizedRoute element={<BulkRestrictionsPage />} />}
                />
              )}
              {isRouteAllowed(links.brokeragePlatformsMT4Swaps.url, permissions) && (
                <Route
                  path={links.brokeragePlatformsMT4Swaps.url}
                  element={<AuthorizedRoute element={<BrokeragePlatformSwapsPage />} />}
                />
              )}
              {isRouteAllowed(links.brokeragePlatformsMT5Swaps.url, permissions) && (
                <Route
                  path={links.brokeragePlatformsMT5Swaps.url}
                  element={<AuthorizedRoute element={<BrokeragePlatformSwapsPage />} />}
                />
              )}
              {isRouteAllowed(links.brokeragePlatformsMT5Sessions.url, permissions) && (
                <Route
                  path={links.brokeragePlatformsMT5Sessions.url}
                  element={<AuthorizedRoute element={<BrokeragePlatformSessionsPage />} />}
                />
              )}
              {isRouteAllowed(links.brokeragePlatformsConfiguration.url, permissions) && (
                <Route
                  path={links.brokeragePlatformsConfiguration.url}
                  element={
                    <AuthorizedRoute element={<BrokeragePlatformServersConfigurationPage />} />
                  }
                />
              )}
              {isRouteAllowed(links.brokerageReferralsRestrictions.url, permissions) && (
                <Route
                  path={links.brokerageReferralsRestrictions.url}
                  element={<AuthorizedRoute element={<ReferralsRestrictionsPage />} />}
                />
              )}
              <Route
                path={links.recordsIntroducingBrokers.detailsUrl as string}
                element={<AuthorizedRoute element={<ResolveIBRoute />} />}
              />
              {isRouteAllowed(links.recordsIntroducingBrokers.url, permissions) && (
                <Route
                  path={links.recordsIntroducingBrokers.url}
                  element={<AuthorizedRoute element={<IntroducingBrokersOverview />} />}
                />
              )}
              <Route
                path={links.recordsPaymentAgents.detailsUrl}
                element={<AuthorizedRoute element={<PaymentAgentDetailsPage />} />}
              />
              {isRouteAllowed(links.recordsPaymentAgents.url, permissions) && (
                <Route
                  path={links.recordsPaymentAgents.url}
                  element={<AuthorizedRoute element={<PaymentAgentsOverview />} />}
                />
              )}
              <Route
                path={links.recordsLeads.detailsUrl}
                element={<AuthorizedRoute element={<ResolveLeadRoute />} />}
              />
              <Route
                path={links.createLead.url}
                element={<AuthorizedRoute element={<CreateLeadPage />} />}
              />
              <Route
                path={links.recordsLeads.url}
                element={<AuthorizedRoute element={<RecordLeadsPage />} />}
              />

              {isRouteAllowed(links.recordsExchangeRates.url, permissions) && (
                <Route
                  path={links.recordsExchangeRates.url}
                  element={<AuthorizedRoute element={<RecordsExchangeRates />} />}
                />
              )}
              {isRouteAllowed(links.brokerageDeals.url, permissions) && (
                <>
                  <Route
                    path={links.brokerageSwapFreeRating.url}
                    element={<AuthorizedRoute element={<SwapFreeRating />} />}
                  />
                  <Route
                    path={links.brokerageDeals.url}
                    element={<AuthorizedRoute element={<RecordDealsPage />} />}
                  />
                </>
              )}
              {isRouteAllowed(links.brokerageInstruments.url, permissions) && (
                <Route
                  path={links.brokerageInstruments.url}
                  element={<AuthorizedRoute element={<InstrumentsPage />} />}
                />
              )}
              {isRouteAllowed(links.brokerageInstruments.url, permissions) && (
                <Route
                  path={links.brokerageLeverages.url}
                  element={<AuthorizedRoute element={<LeveragesPage />} />}
                />
              )}
              {isRouteAllowed(links.brokerageDividends.url, permissions) && (
                <Route
                  path={links.brokerageDividends.url}
                  element={<AuthorizedRoute element={<ResolveDividendsRoute />} />}
                />
              )}

              {isRouteAllowed(links.transactionWallet.url, permissions) && (
                <Route
                  path={links.transactionWallet.url}
                  element={<AuthorizedRoute element={<WalletTransactionsOverview />} />}
                />
              )}

              {isRouteAllowed(links.transactionPaymentAgent.url, permissions) && (
                <Route
                  path={links.transactionPaymentAgent.url}
                  element={<AuthorizedRoute element={<PaymentAgentTransactionsOverview />} />}
                />
              )}
              {isRouteAllowed(links.transactionWalletAdjustments.url, permissions) && (
                <Route
                  path={links.transactionWalletAdjustments.url}
                  element={
                    <AuthorizedRoute element={<WalletSingleAndMultiTransactionsOverview />} />
                  }
                />
              )}
              {isRouteAllowed(links.transactionPlatform.url, permissions) && (
                <Route
                  path={links.transactionPlatform.url}
                  element={<AuthorizedRoute element={<WalletTradingAccountTransferOverview />} />}
                />
              )}
              {isRouteAllowed(links.transactionBonus.url, permissions) && (
                <Route
                  path={links.transactionBonus.url}
                  element={<AuthorizedRoute element={<BonusTransactionsOverview />} />}
                />
              )}
              {isRouteAllowed(links.transactionDepositWithdrawal.url, permissions) && (
                <Route
                  path={links.transactionDepositWithdrawal.url}
                  element={<AuthorizedRoute element={<DepositWithdrawalTransactionOverview />} />}
                />
              )}
              {isRouteAllowed(links.transactionIBCommissions.url, permissions) && (
                <Route
                  path={links.transactionIBCommissions.url}
                  element={<AuthorizedRoute element={<TransactionIBCommissionsOverview />} />}
                />
              )}
              {isRouteAllowed(links.transactionIBCashBacks.url, permissions) && (
                <Route
                  path={links.transactionIBCashBacks.url}
                  element={<AuthorizedRoute element={<TransactionIBCashbacksOverview />} />}
                />
              )}
              {isRouteAllowed(links.transactionIBCashbackSettings.url, permissions) && (
                <Route
                  path={links.transactionIBCashbackSettings.url}
                  element={<AuthorizedRoute element={<TransactionIBCashbackSettingsOverview />} />}
                />
              )}
              {isRouteAllowed(links.transactionUnsubscribeRequests.url, permissions) && (
                <Route
                  path={links.transactionUnsubscribeRequests.url}
                  element={<AuthorizedRoute element={<TransactionUnsubscribeRequestsOverview />} />}
                />
              )}
              <Route
                path={links.transactionTradingAccountAdjustments.url}
                element={
                  <AuthorizedRoute element={<TransactionTradingAccountAdjustmentsOverview />} />
                }
              />
              <Route
                path={links.transactionBulkTransaction.url}
                element={
                  <AuthorizedRoute
                    element={<TransactionBulkTransactionFileUploadHistoryOverview />}
                  />
                }
              />
              <Route
                path={links.transactionBulkWithdrawals.url}
                element={
                  <AuthorizedRoute element={<TransactionBulkTransactionWithdrawalsOverview />} />
                }
              />
              {isRouteAllowed(links.createETDTransaction.url, permissions) && (
                <Route
                  path={links.createETDTransaction.url}
                  element={<AuthorizedRoute element={<TransactionsETDTab />} />}
                />
              )}
              {isRouteAllowed(links.createTransaction.url, permissions) && (
                <Route
                  path={links.createTransaction.url}
                  element={<AuthorizedRoute element={<TransactionsTab />} />}
                />
              )}
              <Route
                path='/transactions/:id'
                element={<AuthorizedRoute element={<ResolveTransactionDetailsRoute />} />}
              />

              <Route
                path={links.ticketsBackoffice.detailsUrl}
                element={<AuthorizedRoute element={<TicketBackOfficeTabs />} />}
              />
              <Route
                path={links.ticketsTranslation.detailsUrl}
                element={<AuthorizedRoute element={<TicketTranslationDetails />} />}
              />
              <Route
                path={links.ticketsBrokerage.detailsUrl}
                element={<AuthorizedRoute element={<TicketBrokerageDetails />} />}
              />
              <Route
                path={links.ticketsPayments.detailsUrl}
                element={<AuthorizedRoute element={<TicketPaymentTabs />} />}
              />
              <Route
                path={links.ticketsCompliance.detailsUrl}
                element={<AuthorizedRoute element={<TicketComplianceTabs />} />}
              />
              <Route
                path={links.ticketsSales.detailsUrl}
                element={<AuthorizedRoute element={<TicketSalesTabs />} />}
              />

              {isRouteAllowed(links.ticketsMyTickets.url, permissions) && (
                <Route
                  path={links.ticketsMyTickets.url}
                  element={<AuthorizedRoute element={<TicketsMyTickets />} />}
                />
              )}

              {isRouteAllowed(links.ticketsTranslation.url, permissions) && (
                <Route
                  path={links.ticketsTranslation.url}
                  element={<AuthorizedRoute element={<TicketsTranslation />} />}
                />
              )}
              {isRouteAllowed(links.ticketsBrokerage.url, permissions) && (
                <Route
                  path={links.ticketsBrokerage.url}
                  element={<AuthorizedRoute element={<TicketsBrokerage />} />}
                />
              )}

              {isRouteAllowed(links.ticketsSales.url, permissions) && (
                <Route
                  path={links.ticketsSales.url}
                  element={<AuthorizedRoute element={<TicketsSales />} />}
                />
              )}
              {isRouteAllowed(links.ticketsPayments.url, permissions) && (
                <Route
                  path={links.ticketsPayments.url}
                  element={<AuthorizedRoute element={<TicketsPayments />} />}
                />
              )}
              {isRouteAllowed(links.ticketsBackoffice.url, permissions) && (
                <Route
                  path={links.ticketsBackoffice.url}
                  element={<AuthorizedRoute element={<TicketsBackOffice />} />}
                />
              )}
              {isRouteAllowed(links.ticketsCompliance.url, permissions) && (
                <Route
                  path={links.ticketsCompliance.url}
                  element={<AuthorizedRoute element={<TicketsCompliance />} />}
                />
              )}

              {isRouteAllowed(links.complianceCountryRisk.url, permissions) && (
                <Route
                  path={links.complianceCountryRisk.url}
                  element={<AuthorizedRoute element={<CountryRiskPage />} />}
                />
              )}
              <Route
                path={links.marketingCampaigns.detailsUrl}
                element={<AuthorizedRoute element={<ResolveCampaignRoute />} />}
              />
              <Route
                path={links.bonusModule.detailsUrl}
                element={<AuthorizedRoute element={<ResolveBonusCampaignRoute />} />}
              />

              {isRouteAllowed(links.disclaimerLossPercentage.url, permissions) && (
                <Route
                  path={links.disclaimerLossPercentage.url}
                  element={<AuthorizedRoute element={<DisclaimerLossPercentagePage />} />}
                />
              )}

              {isRouteAllowed(links.complianceAppropriatenessTests.url, permissions) && [
                <Route
                  path={links.complianceAppropriatenessTests.url}
                  element={<AuthorizedRoute element={<AppropriatenessTestPage />} />}
                />,
                <Route
                  path={links.complianceAppropriatenessTests.detailsUrl}
                  element={<AuthorizedRoute element={<AppropriatenessTestDetailsPage />} />}
                />,
              ]}

              {isRouteAllowed(links.complianceWorldCheckCases.url, permissions) && (
                <Route
                  path={links.complianceWorldCheckCases.url}
                  element={<AuthorizedRoute element={<WorldCheckCasesPage />} />}
                />
              )}

              <Route
                path={links.marketingCampaigns.url}
                element={<AuthorizedRoute element={<CampaignsOverviewPage />} />}
              />
              {isRouteAllowed(links.marketingNotifications.url, permissions) && (
                <Route
                  path={links.marketingNotifications.detailsUrl}
                  element={<AuthorizedRoute element={<ResolveNotificationRoute />} />}
                />
              )}
              {isRouteAllowed(links.marketingNotifications.url, permissions) && (
                <Route
                  path={links.marketingNotifications.url}
                  element={<AuthorizedRoute element={<MarketingNotificationsOverviewPage />} />}
                />
              )}

              {isRouteAllowed(links.rewardTypeManager.url, permissions) && (
                <Route
                  path={links.rewardTypeManager.url}
                  element={<AuthorizedRoute element={<RewardsTypeManager />} />}
                />
              )}

              {isRouteAllowed(links.rewardPrograms.url, permissions) && (
                <Route
                  path={links.rewardPrograms.url}
                  element={<AuthorizedRoute element={<RewardPrograms />} />}
                />
              )}

              {isRouteAllowed(links.rewardPrograms.url, permissions) && (
                <Route
                  path={links.rewardPrograms.detailsUrl}
                  element={<AuthorizedRoute element={<ResolveRewardPrograms />} />}
                />
              )}

              {isRouteAllowed(
                links.administrationMlWithdrawalAutomationGlobalControls.url,
                permissions
              ) && (
                <Route
                  path={links.administrationMlWithdrawalAutomationGlobalControls.url}
                  element={
                    <AuthorizedRoute element={<MLWithdrawalAutomationGlobalControlsPage />} />
                  }
                />
              )}

              {isRouteAllowed(
                links.administrationMlWithdrawalAutomationOverrides.url,
                permissions
              ) && (
                <Route
                  path={links.administrationMlWithdrawalAutomationOverrides.url}
                  element={<AuthorizedRoute element={<MLWithdrawalAutomationOverridesPage />} />}
                />
              )}

              {isRouteAllowed(links.ibLanding.url, permissions) && (
                <Route
                  path={links.ibLanding.url}
                  element={<AuthorizedRoute element={<IbLandingPage />} />}
                />
              )}
              {isRouteAllowed(links.ibLanding.url, permissions) && (
                <Route
                  path={links.ibLanding.detailsUrl}
                  element={<AuthorizedRoute element={<IbLandingDetailsPage />} />}
                />
              )}
              {isRouteAllowed(links.referralMaterials.url, permissions) && (
                <Route
                  path={links.referralMaterials.url}
                  element={<AuthorizedRoute element={<ReferralMaterialsPage />} />}
                />
              )}
              {isRouteAllowed(links.referralMaterials.url, permissions) && (
                <Route
                  path={links.referralMaterials.detailsUrl}
                  element={<AuthorizedRoute element={<ReferralMaterialsDetailsPage />} />}
                />
              )}
              {isRouteAllowed(links.referralMaterialsCategories.url, permissions) && (
                <Route
                  path={links.referralMaterialsCategories.url}
                  element={<AuthorizedRoute element={<ReferralMaterialsCategoriesPage />} />}
                />
              )}
              {isRouteAllowed(links.referralMaterialsCategories.url, permissions) && (
                <Route
                  path={links.referralMaterialsCategories.detailsUrl}
                  element={<AuthorizedRoute element={<ReferralMaterialsCategoryDetailsPage />} />}
                />
              )}
              {isRouteAllowed(links.bonusModule.url, permissions) && (
                <Route
                  path={links.bonusModule.url}
                  element={<AuthorizedRoute element={<BonusCampaignModulePage />} />}
                />
              )}

              {isRouteAllowed(links.sglLinkGenerator.url, permissions) && (
                <Route
                  path={links.sglLinkGenerator.url}
                  element={<AuthorizedRoute element={<SGLLinkGeneratorPage />} />}
                />
              )}
              <Route path={links.emails.url} element={<AuthorizedRoute element={<Emails />} />} />
              {isRouteAllowed(links.productVersions.url, permissions) && (
                <Route
                  path={links.productVersions.url}
                  element={<AuthorizedRoute element={<ProductVersions />} />}
                />
              )}
              {isRouteAllowed(links.productFeatureFlags.url, permissions) && (
                <Route
                  path={links.productFeatureFlags.url}
                  element={<AuthorizedRoute element={<FeatureFlags />} />}
                />
              )}
              {isRouteAllowed(links.productFeatureFlags.url, permissions) && (
                <Route
                  path={links.productFeatureFlags.detailsUrl}
                  element={<AuthorizedRoute element={<FeatureFlagDetailsPage />} />}
                />
              )}
              {isRouteAllowed(links.productPlatformVersions.url, permissions) && (
                <Route
                  path={links.productPlatformVersions.url}
                  element={<AuthorizedRoute element={<PlatformVersions />} />}
                />
              )}

              {isRouteAllowed(links.administrationUsers.url, permissions) && (
                <Route
                  path={links.administrationUsers.url}
                  element={<AuthorizedRoute element={<AdministrationUsers />} />}
                />
              )}
              {isRouteAllowed(links.administrationUserTeams.url, permissions) && (
                <Route
                  path={links.administrationUserTeams.url}
                  element={<AuthorizedRoute element={<AdministrationUserTeams />} />}
                />
              )}
              {isRouteAllowed(links.administrationTeams.url, permissions) && (
                <Route
                  path={links.administrationTeams.url}
                  element={<AuthorizedRoute element={<Teams />} />}
                />
              )}

              {isRouteAllowed(links.administrationRoles.url, permissions) && (
                <Route
                  path={links.administrationRoles.url}
                  element={<AuthorizedRoute element={<RolesPage />} />}
                />
              )}
              {isRouteAllowed(links.administrationCountries.url, permissions) && (
                <Route
                  path={links.administrationCountries.url}
                  element={<AuthorizedRoute element={<Countries />} />}
                />
              )}

              <Route
                path={links.administrationProducts.url}
                element={<AuthorizedRoute element={<AdministrationCountriesProducts />} />}
              />
              {isRouteAllowed(links.administrationPaymentProvidersConfig.url, permissions) && (
                <Route
                  path={links.administrationPaymentProvidersConfig.url}
                  element={<AuthorizedRoute element={<PaymentProvidersConfigPage />} />}
                />
              )}
              {isRouteAllowed(links.administrationPaymentProvidersConfig.url, permissions) && (
                <Route
                  path={links.administrationPaymentProvidersConfig.detailsUrl}
                  element={<AuthorizedRoute element={<ResolvePaymentProviderSlug />} />}
                />
              )}
              {isRouteAllowed(links.administrationPaymentProvidersOrdering.url, permissions) && (
                <Route
                  path={links.administrationPaymentProvidersOrdering.url}
                  element={<AuthorizedRoute element={<PaymentProvidersOrderingPage />} />}
                />
              )}
              {isRouteAllowed(links.administrationPaymentProvidersOrdering.url, permissions) && (
                <Route
                  path={links.administrationPaymentProvidersOrdering.detailsUrl}
                  element={<AuthorizedRoute element={<div>TODO</div>} />}
                />
              )}

              {isRouteAllowed(links.administrationSubscriptions.url, permissions) && (
                <Route
                  path={links.administrationSubscriptions.url}
                  element={<AuthorizedRoute element={<SubscriptionsPage />} />}
                />
              )}
              {isRouteAllowed(links.administrationSubscriptions.url, permissions) && (
                <Route
                  path={links.administrationSubscriptions.detailsUrl}
                  element={<AuthorizedRoute element={<ResolveSubscriptionRoute />} />}
                />
              )}
              {isRouteAllowed(links.administrationWallets.url, permissions) && (
                <Route
                  path={links.administrationWallets.url}
                  element={<AuthorizedRoute element={<AdministrationWallets />} />}
                />
              )}
              {isRouteAllowed(links.administrationTradingAccounts.url, permissions) && (
                <Route
                  path={links.administrationTradingAccounts.url}
                  element={<AuthorizedRoute element={<TradingAccountsCountries />} />}
                />
              )}
              {isRouteAllowed(
                links.administrationAllowedPaymentProvidersMatrix.url,
                permissions
              ) && (
                <Route
                  path={links.administrationAllowedPaymentProvidersMatrix.url}
                  element={<AuthorizedRoute element={<AllowedPaymentProviders />} />}
                />
              )}
              {isRouteAllowed(
                links.administrationAllowedPaymentProvidersUncovered.url,
                permissions
              ) && (
                <Route
                  path={links.administrationAllowedPaymentProvidersUncovered.url}
                  element={<AuthorizedRoute element={<AllowedPaymentProviders uncovered />} />}
                />
              )}
              {isRouteAllowed(
                links.administrationPaymentControlsPSPCheckState.url,
                permissions
              ) && (
                <Route
                  path={links.administrationPaymentControlsPSPCheckState.detailsUrl}
                  element={
                    <AuthorizedRoute element={<PaymentControlsPSPCheckStateDetailsPage />} />
                  }
                />
              )}
              {isRouteAllowed(links.administrationInterestRateConfigControls.url, permissions) && (
                <Route
                  path={links.administrationInterestRateConfigControls.url}
                  element={<AuthorizedRoute element={<InterestRatesWalletsConfigurationPage />} />}
                />
              )}
              {isRouteAllowed(links.administrationInterestRateThresholds.url, permissions) && (
                <Route
                  path={links.administrationInterestRateThresholds.url}
                  element={<AuthorizedRoute element={<InterestRatesThresholdsPage />} />}
                />
              )}
              {isRouteAllowed(links.administrationInterestRateEligibility.url, permissions) && (
                <Route
                  path={links.administrationInterestRateEligibility.url}
                  element={<AuthorizedRoute element={<InterestRatesEligibilityPage />} />}
                />
              )}
              {isRouteAllowed(
                links.administrationPaymentControlsPSPCheckState.url,
                permissions
              ) && (
                <Route
                  path={links.administrationPaymentControlsPSPCheckState.url}
                  element={<AuthorizedRoute element={<PaymentControlsPSPCheckStatePage />} />}
                />
              )}
              {isRouteAllowed(links.administrationWithdrawalThresholds.url, permissions) && (
                <Route
                  path={links.administrationWithdrawalThresholds.url}
                  element={<AuthorizedRoute element={<WithdrawalThresholdsPage />} />}
                />
              )}
              {isFeatureEnabled(TenantFeature.administrationTransactionProcessingMetrics) && (
                <Route
                  path={links.administrationTransactionProcessingMetrics.url}
                  element={<AuthorizedRoute element={<TransactionProcessingMetricsPage />} />}
                />
              )}

              {isRouteAllowed(links.administrationRateMarkups.url, permissions) && (
                <Route
                  path={links.administrationRateMarkups.url}
                  element={<AuthorizedRoute element={<RateMarkupsPage />} />}
                />
              )}
              {isRouteAllowed(links.administrationPlatformLinks.url, permissions) && (
                <Route
                  path={links.administrationPlatformLinks.url}
                  element={<AuthorizedRoute element={<PlatformDownloadLinksPage />} />}
                />
              )}
              {isRouteAllowed(links.administrationContactDetails.url, permissions) && (
                <Route
                  path={links.administrationContactDetails.url}
                  element={<AuthorizedRoute element={<ContactDetailsPage />} />}
                />
              )}
              {isRouteAllowed(links.recordsCurrencyMarkups.url, permissions) && (
                <Route
                  path={links.recordsCurrencyMarkups.url}
                  element={<AuthorizedRoute element={<RecordsCurrencyMarkups />} />}
                />
              )}
              <Route path='*' element={<Navigate to={links.dashboard.url} />} />
              <Route path='/' element={<Dashboard />} />
            </>
          )}
        </Routes>
      </MainLayout>
      <Toaster closeButton richColors position='top-right' swipeDirections={[]} />
    </Suspense>
  )
}

export const Routing: React.FC = () => {
  return <MainContainer />
}
